import type { BlogArticle, BlogArticleFrontmatter, BlogAuthor, BlogEvidenceSource, BlogFaqItem } from '../types/blog'

interface RawBlogItem extends BlogArticleFrontmatter {
  path: string
  body?: unknown
  meta?: Record<string, unknown>
}

interface BlogQueryBuilder {
  where(field: string, operator: string, value: unknown): BlogQueryBuilder
  order(field: string, direction: 'ASC' | 'DESC'): BlogQueryBuilder
  all(): Promise<RawBlogItem[]>
}

declare function queryCollection(collection: 'blog'): BlogQueryBuilder

const DEFAULT_AUTHOR_NAME = 'LuminAI Team'
const READING_WORDS_PER_MINUTE = 200

function getString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
}

function getBoolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function getNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value) : undefined
}

function stripHtml(input: string): string {
  return input.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
}

export function sanitizeExcerpt(input?: string, maxLength = 200): string {
  if (!input) return ''
  const clean = stripHtml(input)
  if (clean.length <= maxLength) return clean
  return `${clean.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`
}

export function extractPlainText(body: unknown): string {
  if (!body) return ''

  const parts: string[] = []

  const walk = (node: unknown): void => {
    if (typeof node === 'string') {
      parts.push(node)
      return
    }
    if (Array.isArray(node)) {
      for (const item of node) walk(item)
      return
    }
    if (!node || typeof node !== 'object') return

    const record = node as Record<string, unknown>
    if (typeof record.value === 'string') parts.push(record.value)
    if ('children' in record) walk(record.children)
  }

  walk(body)

  const text = parts.join(' ').replace(/\s+/g, ' ').trim()
  return stripHtml(text)
}

export function estimateReadingTimeMinutes(text: string): number {
  const words = text.trim().split(/\s+/).filter(Boolean).length
  if (words === 0) return 1
  return Math.max(1, Math.ceil(words / READING_WORDS_PER_MINUTE))
}

function toIsoDate(value?: string): string | undefined {
  if (!value) return undefined
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return undefined
  return date.toISOString()
}

function resolveAuthor(author: unknown): BlogAuthor {
  if (typeof author === 'string' && author.trim().length > 0) {
    return { name: author.trim() }
  }

  if (author && typeof author === 'object') {
    const authorRecord = author as Record<string, unknown>
    const name = getString(authorRecord.name)
    if (name) {
      return {
        name,
        title: getString(authorRecord.title) ?? getString(authorRecord.expertise),
        expertise: getString(authorRecord.expertise) ?? getString(authorRecord.title),
        bio: getString(authorRecord.bio),
      }
    }
  }

  return { name: DEFAULT_AUTHOR_NAME }
}

function resolveSlug(item: RawBlogItem): string {
  const explicitSlug = getString(item.slug) ?? getString(item.meta?.slug)
  if (explicitSlug) return explicitSlug

  return item.path
    .replace(/^\/blog\//, '')
    .replace(/\/$/, '')
    .trim()
}

function resolveCategories(item: RawBlogItem): string[] {
  if (Array.isArray(item.categories) && item.categories.length > 0) {
    return item.categories.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
  }

  const singleCategory = getString(item.category) ?? getString(item.meta?.category)
  return singleCategory ? [singleCategory] : []
}

function resolveProduct(item: RawBlogItem): string | null {
  return getString(item.product) ?? getString(item.meta?.product) ?? null
}

function resolvePublishedAt(item: RawBlogItem): string {
  const value =
    getString(item.publishedAt) ??
    getString(item.date) ??
    getString(item.meta?.publishedAt) ??
    getString(item.meta?.date)

  return toIsoDate(value) ?? new Date(0).toISOString()
}

function resolveFaq(item: RawBlogItem): BlogFaqItem[] {
  // Support both `faq` and `faqItems` frontmatter field names
  const legacyFaqItems = (item as RawBlogItem & { faqItems?: unknown }).faqItems
  const source: unknown[] = Array.isArray(item.faq)
    ? item.faq
    : Array.isArray(legacyFaqItems)
      ? legacyFaqItems
      : []

  if (source.length === 0) return []

  return source
    .map((entry: unknown) => {
      if (!entry || typeof entry !== 'object') return null
      const record = entry as Record<string, unknown>
      const question = getString(record.question)
      const answer = getString(record.answer)
      return question && answer ? { question, answer } : null
    })
    .filter((entry: BlogFaqItem | null): entry is BlogFaqItem => entry !== null)
}

function resolveFocusQuestion(item: RawBlogItem): string | null {
  return (
    getString(item.focus_question) ??
    getString(item.focusQuestion) ??
    getString(item.meta?.focus_question) ??
    getString(item.meta?.focusQuestion) ??
    null
  )
}

function resolveEvidenceSource(entry: unknown): BlogEvidenceSource | null {
  if (typeof entry === 'string') {
    const url = getString(entry)
    return url ? { url } : null
  }

  if (!entry || typeof entry !== 'object') return null

  const record = entry as Record<string, unknown>
  const url =
    getString(record.url) ??
    getString(record.href) ??
    getString(record.source) ??
    getString(record.link)

  if (!url) return null

  return {
    url,
    label: getString(record.label) ?? getString(record.name) ?? getString(record.title),
    note: getString(record.note) ?? getString(record.description),
  }
}

function resolveEvidenceSources(item: RawBlogItem): BlogEvidenceSource[] {
  const sources = item.evidence_sources ?? item.evidenceSources ?? item.meta?.evidence_sources ?? item.meta?.evidenceSources
  if (!Array.isArray(sources)) return []

  return sources
    .map(resolveEvidenceSource)
    .filter((entry): entry is BlogEvidenceSource => entry !== null)
}

function resolveLastUpdated(item: RawBlogItem): string | null {
  const value = getString(item.last_updated) ?? getString(item.meta?.last_updated)
  return toIsoDate(value) ?? null
}

function resolveDescription(item: RawBlogItem): string {
  return (
    getString(item.description) ??
    getString(item.excerpt) ??
    getString(item.meta?.description) ??
    getString(item.meta?.excerpt) ??
    ''
  )
}

export function normalizeBlogArticle(item: RawBlogItem): BlogArticle {
  const publishedAt = resolvePublishedAt(item)
  const plainText = extractPlainText(item.body)
  const description = resolveDescription(item)
  const excerpt = sanitizeExcerpt(description || plainText, 200)
  const readingTime =
    getNumber(item.reading_time) ??
    getNumber(item.readingTime) ??
    getNumber(item.meta?.reading_time) ??
    getNumber(item.meta?.readingTime) ??
    estimateReadingTimeMinutes(plainText)

  return {
    path: item.path,
    title: getString(item.title) ?? 'Untitled',
    slug: resolveSlug(item),
    date: publishedAt,
    publishedAt,
    lastUpdated: resolveLastUpdated(item),
    author: resolveAuthor(item.author ?? item.meta?.author),
    product: resolveProduct(item),
    categories: resolveCategories(item),
    tags: Array.isArray(item.tags)
      ? item.tags.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
      : [],
    excerpt,
    description,
    heroImage: getString(item.hero_image) ?? getString(item.meta?.hero_image) ?? null,
    draft: getBoolean(item.draft, false),
    archived: getBoolean(item.archived ?? item.meta?.archived, false),
    readingTime,
    plainText,
    faq: resolveFaq(item),
    focusQuestion: resolveFocusQuestion(item),
    evidenceSources: resolveEvidenceSources(item),
  }
}

function isPublished(article: BlogArticle): boolean {
  if (article.draft || article.archived) return false
  return new Date(article.publishedAt).getTime() <= Date.now()
}

function sortReverseChronological(items: BlogArticle[]): BlogArticle[] {
  return [...items].sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
}

async function queryAllArticles(): Promise<BlogArticle[]> {
  const rows = await queryCollection('blog').where('draft', '=', false).all()
  return rows.map(normalizeBlogArticle)
}

export async function queryPublishedArticles(): Promise<BlogArticle[]> {
  return sortReverseChronological((await queryAllArticles()).filter(isPublished))
}

export async function getArticleBySlug(slug: string): Promise<BlogArticle | null> {
  const target = slug.replace(/^\/blog\//, '').replace(/\/$/, '').trim()
  if (!target) return null

  const articles = await queryPublishedArticles()
  return articles.find((article) => article.slug === target) ?? null
}

export function useBlogContent() {
  return {
    queryPublishedArticles,
    getArticleBySlug,
    sanitizeExcerpt,
    extractPlainText,
    estimateReadingTimeMinutes,
    normalizeBlogArticle,
  }
}
